import React from 'react';
import { Box, Typography, Paper, Avatar } from '@mui/material';
import {
  People as ConnectionsIcon,
  Merge as MergeIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: Connections concept
const ConnectionsConceptVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>You</Avatar>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ width: 60, height: 2, bgcolor: 'primary.main' }} />
        <Box sx={{ width: 60, height: 2, bgcolor: 'primary.main' }} />
        <Box sx={{ width: 60, height: 2, bgcolor: 'primary.main' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32, fontSize: 12 }}>JD</Avatar>
        <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32, fontSize: 12 }}>MK</Avatar>
        <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32, fontSize: 12 }}>TS</Avatar>
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Your address book for squares
    </Typography>
  </Box>
);

// Visual: Auto-creation
const AutoCreationVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
      <Paper elevation={1} sx={{ p: 1, minWidth: 50, textAlign: 'center' }}>
        <Typography variant="caption" display="block">Board 1</Typography>
        <Typography variant="caption" color="primary.main" fontWeight="bold">John</Typography>
      </Paper>
      <Typography variant="body2" color="text.secondary">+</Typography>
      <Paper elevation={1} sx={{ p: 1, minWidth: 50, textAlign: 'center' }}>
        <Typography variant="caption" display="block">Board 2</Typography>
        <Typography variant="caption" color="primary.main" fontWeight="bold">John</Typography>
      </Paper>
      <Typography variant="body2" color="text.secondary">=</Typography>
      <Paper elevation={2} sx={{ p: 1, minWidth: 60, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main', textAlign: 'center' }}>
        <ConnectionsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="caption" display="block" color="primary.main" fontWeight="bold">Connected!</Typography>
      </Paper>
    </Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      2+ shared boards creates a connection
    </Typography>
  </Box>
);

// Visual: Quick access
const QuickAccessVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">Without Connections</Typography>
      <Typography variant="body2" color="error.main">Search, invite, wait...</Typography>
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">With Connections</Typography>
      <Typography variant="body2" color="success.main">One click to add!</Typography>
    </Box>
  </Box>
);

// Visual: Merge flow
const MergeFlowVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
      <Paper elevation={0} sx={{ p: 1, bgcolor: 'grey.100', textAlign: 'center' }}>
        <Typography variant="caption" display="block" fontSize={10}>Offline</Typography>
        <Typography variant="caption" fontWeight="bold">"John D."</Typography>
      </Paper>
      <MergeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
      <Paper elevation={0} sx={{ p: 1, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main', textAlign: 'center' }}>
        <Typography variant="caption" display="block" fontSize={10}>Connection</Typography>
        <Typography variant="caption" fontWeight="bold" color="primary.main">@john_doe</Typography>
      </Paper>
    </Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Merge offline players into real accounts
    </Typography>
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Player Connections',
    description: 'Connections remember the players you work with frequently. Think of it as your SquareUp address book for quickly adding people to boards.',
    icon: <ConnectionsIcon sx={{ fontSize: 40 }} />,
    visual: <ConnectionsConceptVisual />,
  },
  {
    title: 'Created Automatically',
    description: 'When a player participates in two or more of your boards, they become a connection automatically. No manual setup required!',
    icon: <AutoIcon sx={{ fontSize: 40 }} />,
    visual: <AutoCreationVisual />,
  },
  {
    title: 'Quick Player Access',
    description: 'Connections appear at the top when adding players to boards. One click to add them - no searching or waiting for invites.',
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    visual: <QuickAccessVisual />,
  },
  {
    title: 'Smart Merge',
    description: 'See an offline player who might be a connection? Merge them together! Their squares transfer to the real account across all boards.',
    icon: <MergeIcon sx={{ fontSize: 40 }} />,
    visual: <MergeFlowVisual />,
  },
];

interface ConnectionsEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const ConnectionsEducationModal: React.FC<ConnectionsEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="connections"
    steps={steps}
    headerColor="primary.main"
    finishButtonText="View Connections"
    buttonColor="primary"
  />
);

export default ConnectionsEducationModal;
